 import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { useState, useEffect } from 'react'
import { ChevronLeft, Shield } from 'lucide-react'

interface Plan {
  name: string
  monthlyPrice: number
  yearlyPrice: number
  description: string
  features: string[]
}

export default function PlanSelection() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, refreshUser } = useAuth()
  
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'pro' | 'premium' | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [loading, setLoading] = useState(false)
  const [promoApplied, setPromoApplied] = useState(false)
  const [promoDiscount, setPromoDiscount] = useState(0)
  const [promoCode, setPromoCode] = useState('')

  const userEmail = (location.state as any)?.email || user?.email || ''
  const currentUserPlan = (user?.selectedPlan || user?.plan || 'starter').toLowerCase()
  
  // Check if user already has a plan (from previous selection or payment)
  const hasExistingPlan = !!user?.selectedPlan || !!user?.plan

  useEffect(() => {
    const stored = localStorage.getItem('appliedPromo')
    if (stored) {
      try {
        const promo = JSON.parse(stored)
        setPromoApplied(true)
        setPromoDiscount(promo.discountPercent || 10)
        setPromoCode(promo.code || '')
      } catch (e) {}
    }
  }, [])

  const plans: Record<'starter' | 'pro' | 'premium', Plan> = {
    starter: {
      name: 'Starter',
      monthlyPrice: 29,
      yearlyPrice: 21,
      description: 'For individuals getting started',
      features: [
        'AI Email Generation - Unlimited',
        'All 541 Niche Profiles',
        'Compliance Tools',
        'Community Access',
        'Share Links - Unlimited',
        '1,000 Subscribers',
        '5 Templates',
        '1 Team Member',
        'Email Tracking - 5/mo',
        'Smart Brief - 10/mo',
        'Basic Analytics'
      ]
    },
    pro: {
      name: 'Professional',
      monthlyPrice: 99,
      yearlyPrice: 79,
      description: 'For growing businesses',
      features: [
        'Everything in Starter',
        '10,000 Subscribers',
        '20 Templates',
        '5 Team Members',
        'Email Tracking - 200/mo',
        'Smart Brief - 30/mo',
        'Behavioral Learning - Advanced',
        'API Access - 20/mo',
        'Advanced Analytics',
        'Priority Support'
      ]
    },
    premium: {
      name: 'Enterprise',
      monthlyPrice: 299,
      yearlyPrice: 249,
      description: 'For large organizations',
      features: [
        'Everything in Pro',
        '50,000+ Subscribers',
        'Unlimited Templates',
        'Unlimited Team Members',
        'Email Tracking - Unlimited',
        'Smart Brief - 50/mo',
        'Behavioral Learning - Full',
        'API Access - 50/mo'
      ]
    }
  }

  const getPrice = (plan: Plan) => {
    const base = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
    if (promoApplied) return Math.round(base * (1 - promoDiscount / 100))
    return base
  }

  const getOriginalPrice = (plan: Plan) => billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
  const getYearlySavings = (plan: Plan) => (plan.monthlyPrice * 12) - (plan.yearlyPrice * 12)
  const getSavingsPercentage = (plan: Plan) => Math.round((1 - plan.yearlyPrice / plan.monthlyPrice) * 100)

  // ✅ NEW: Handle staying on current plan
  const handleStayOnCurrentPlan = () => {
    // Navigate directly to dashboard without payment
    navigate('/dashboard')
    toast.success('Keeping your current plan!')
  }

  // ✅ NEW: Handle going back to previous page
  const handleGoBack = () => {
    if (hasExistingPlan) {
      // If user already has a plan, ask if they want to go back to dashboard
      if (window.confirm('Go back to dashboard? Your current plan will remain unchanged.')) {
        navigate('/dashboard')
      }
    } else {
      navigate(-1)
    }
  }

  const handleSelectPlan = (planKey: 'starter' | 'pro' | 'premium') => {
    // ✅ If user selects the SAME plan they already have
    if (planKey === currentUserPlan) {
      toast.success(`You're already on the ${plans[planKey].name} plan!`)
      navigate('/dashboard')
      return
    }

    if (!userEmail) {
      toast.error('Email not found. Please sign up first.')
      navigate('/signup')
      return
    }

    const plan = plans[planKey]
    const price = getPrice(plan)

    navigate('/payment', {
      state: {
        plan: planKey === 'premium' ? 'enterprise' : planKey,
        planName: plan.name,
        billingCycle,
        price,
        email: userEmail,
        promoApplied,
        promoCode,
        promoDiscount,
        currentPlan: currentUserPlan // Pass current plan to payment page
      }
    })
  }

  // Get plan display name
  const getPlanDisplayName = (plan: string) => {
    const names: Record<string, string> = { starter: 'Starter', pro: 'Professional', premium: 'Enterprise' }
    return names[plan] || plan
  }

  const planOrder: ('starter' | 'pro' | 'premium')[] = ['starter', 'pro', 'premium']

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <button onClick={handleGoBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-8">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>

        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600">Select the perfect plan for your email marketing needs</p>
          {userEmail && <p className="text-sm text-gray-500 mt-2">Account: <span className="font-semibold">{userEmail}</span></p>}
          
          {/* ✅ Show current plan badge */}
          {hasExistingPlan && (
            <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
              <Shield className="w-4 h-4" />
              Current Plan: {getPlanDisplayName(currentUserPlan)}
            </div>
          )}
          
          {promoApplied && (
            <div className="inline-block mt-3 px-4 py-2 bg-green-100 text-green-700 rounded-full font-semibold text-sm ml-2">
              🎁 {promoDiscount}% discount applied! Code: {promoCode}
            </div>
          )}
        </div>

        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl p-1.5 shadow-md inline-flex">
            <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2.5 rounded-lg font-semibold ${billingCycle === 'monthly' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>Monthly</button>
            <button onClick={() => setBillingCycle('yearly')} className={`px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
              Yearly <span className={`text-xs px-2 py-0.5 rounded-full ${billingCycle === 'yearly' ? 'bg-green-400 text-gray-900' : 'bg-green-100 text-green-700'}`}>Save 25-28%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
          {planOrder.map((planKey) => {
            const plan = plans[planKey]
            const price = getPrice(plan)
            const originalPrice = getOriginalPrice(plan)
            const isPro = planKey === 'pro'
            const hasDiscount = promoApplied && price < originalPrice
            const isCurrentPlan = planKey === currentUserPlan

            return (
              <div key={planKey} className={`bg-white rounded-2xl shadow-lg p-8 border-2 relative ${isPro ? 'border-blue-400 transform scale-105 bg-gradient-to-br from-blue-600 to-indigo-700 text-white' : 'border-gray-100'} transition-all duration-300 hover:shadow-xl`}>
                {isPro && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-6 py-1.5 text-sm font-bold rounded-full shadow-lg">⭐ Most Popular</div>}
                
                {/* ✅ Current plan badge */}
                {isCurrentPlan && hasExistingPlan && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    Current
                  </div>
                )}

                <h3 className={`text-2xl font-bold mb-2 ${isPro ? 'mt-4' : ''}`}>{plan.name}</h3>
                <p className={`mb-4 ${isPro ? 'text-blue-100' : 'text-gray-600'}`}>{plan.description}</p>

                <div className="mb-2">
                  {hasDiscount && (
                    <span className={`text-lg line-through mr-2 ${isPro ? 'text-blue-300' : 'text-gray-400'}`}>${originalPrice}</span>
                  )}
                  <span className="text-4xl font-bold">${price}</span>
                  <span className={`text-lg font-normal ${isPro ? 'text-blue-200' : 'text-gray-500'}`}>/mo</span>
                </div>

                {hasDiscount && (
                  <div className="mb-2 px-3 py-1 rounded-lg text-sm font-medium inline-block bg-green-500 text-white">
                    🎁 {promoDiscount}% off
                  </div>
                )}

                {billingCycle === 'yearly' && (
                  <div className={`mb-4 px-3 py-1.5 rounded-lg text-sm font-medium inline-block ${isPro ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700'}`}>
                    🎉 Save {getSavingsPercentage(plan)}% (${getYearlySavings(plan)}/year)
                  </div>
                )}

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className={`mt-1 ${isPro ? 'text-blue-200' : 'text-green-500'}`}>✓</span>
                      <span className={isPro ? 'text-white' : 'text-gray-700'}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleSelectPlan(planKey)} 
                  disabled={loading || isCurrentPlan}
                  className={`w-full text-center py-3 rounded-lg transition-colors font-semibold ${
                    isCurrentPlan ? 'bg-gray-300 cursor-not-allowed text-gray-600' :
                    isPro ? 'bg-white text-blue-600 hover:bg-gray-100' :
                    planKey === 'premium' ? 'bg-gray-800 text-white hover:bg-gray-900' :
                    'bg-blue-600 text-white hover:bg-blue-700'
                  }`}>
                  {isCurrentPlan ? '✓ Current Plan' : loading && selectedPlan === planKey ? 'Processing...' : `Choose ${plan.name}`}
                </button>
              </div>
            )
          })}
        </div>

        {/* ✅ NEW BUTTON - Stay on Current Plan */}
        {hasExistingPlan && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleStayOnCurrentPlan}
              className="px-8 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Shield className="w-5 h-5" />
              Stay on Current Plan → Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  )
} 